import { get24HourTime, getShowNetworkTimezone as showNetworkTime } from '@utils/general'
import { getDisplayTimezoneString as showTimezoneString } from '@utils/general'
import dayjs, { type Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import minmax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(minmax)
dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)

export type DateOrMoment = Date | moment.Moment | Dayjs | string
export type Timezone = Intl.DateTimeFormatOptions['timeZone']

export const formatters = {
  WITHOUT_TIME: (d: DateOrMoment, _timeZone: Timezone = undefined) => {
    const date = dayjs(dateOrMomentToDate(d)).startOf('day').format('YYYY-MM-DDT00:00:00')
    return date
  },
  DATE: (d: DateOrMoment, timeZone: Timezone = undefined, overrideSelectedTime = false) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date)
        return new Intl.DateTimeFormat(undefined, {
          timeZone: overrideSelectedTime ? timeZone : showNetworkTime() ? timeZone : undefined,
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).format(date) // 01/01/2020
    } catch (e) {
      console.error('DATE: Invalid Date Format', { d })
    }
    return ''
  },
  TIME: (d: DateOrMoment | undefined, timeZone: Timezone = undefined, overrideSelectedTime = false) => {
    try {
      const date = d ? dateOrMomentToDate(d) : undefined
      if (date) {
        const newDate = new Intl.DateTimeFormat(undefined, {
          timeZone: overrideSelectedTime ? timeZone : showNetworkTime() ? timeZone : undefined,
          hour: 'numeric',
          minute: '2-digit',
          hour12: !get24HourTime(),
          timeZoneName: showTimezoneString() ? 'short' : undefined,
        }).format(date)
        return newDate?.toUpperCase()
      }
    } catch (e) {
      console.error('Invalid Time Format')
    }
    return ''
  },
  // Time with on-the-hour minutes dropped: "1 PM" instead of "1:00 PM" (keeps "1:30 PM").
  TIME_COMPACT: (d: DateOrMoment | undefined, timeZone: Timezone = undefined, overrideSelectedTime = false) =>
    formatters.TIME(d, timeZone, overrideSelectedTime).replace(/:00(?=\s|$)/, ''),
  // Compact time with a single-letter meridiem, for more compact UI: "5p" / "5:45p".
  TIME_EXTRA_COMPACT: (d: DateOrMoment | undefined, timeZone: Timezone = undefined, overrideSelectedTime = false) =>
    formatters
      .TIME_COMPACT(d, timeZone, overrideSelectedTime)
      .replace(/\s*AM\b/, 'a')
      .replace(/\s*PM\b/, 'p'),
  TIME24: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: showTimezoneString() ? 'short' : undefined,
    }).format(dateOrMomentToDate(d)),
  // Takes an hour, not an instant, so it deliberately has no timeZone argument: the
  // caller has already resolved the wall clock it belongs to and names it elsewhere.
  // UTC is pinned on both sides purely so the hour survives the round trip through
  // Intl untouched by whatever zone the browser is in.
  // hour12: false resolves to the h24 cycle, which renders midnight as "24". h23 has to
  // be asked for by name, and only in place of hour12, which overrides it when present.
  HOUR_OF_DAY: (hour: number) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC',
      hour: 'numeric',
      ...(get24HourTime() ? { hourCycle: 'h23' as const } : { hour12: true }),
    })
      .format(new Date(Date.UTC(2000, 0, 1, hour)))
      .toUpperCase(), // 8 AM
  DAY: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      weekday: 'short',
    }).format(dateOrMomentToDate(d)), // Fri
  LONG_DAY: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      weekday: 'long',
    }).format(dateOrMomentToDate(d)), // Friday
  SHORT_DATE: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date)
        return new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        }).format(date) // 01/01/20
    } catch (e) {
      console.error('DATE: Invalid Date Format', { d })
    }
  },
  SHORT_DATE_TIME: (d: DateOrMoment, timeZone: Timezone = undefined, overrideSelectedTime = false) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date)
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: overrideSelectedTime ? timeZone : showNetworkTime() ? timeZone : undefined,
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date) // 1/1, 9:30 PM
        )
    } catch (e) {
      console.error('Invalid Date Time Format from SHORT_DATE_TIME', { d })
    }
  },
  // Used by Directive charts in Supply / Directives
  CHART_DAY_HOUR: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        const formatter = new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          day: 'numeric',
          hour: 'numeric',
          timeZoneName: showTimezoneString() ? 'short' : undefined,
        }).formatToParts(date) // 1-9PM
        return `${formatter?.[0]?.value}-${formatter?.[2]?.value}${
          formatter?.[4]?.value
        } ${formatter?.[6]?.value ?? ''}`.trim()
      }
    } catch (e) {
      console.error('Invalid Date Time Format from DAY_HOUR', { d })
    }
  },
  MONTH_DAY: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      month: 'numeric',
      day: 'numeric',
    }).format(dateOrMomentToDate(d)), // 1/1
  SHORT_MONTH_DAY: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      month: 'short',
      day: 'numeric',
    }).format(dateOrMomentToDate(d)),
  DATE_24_TIME: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZoneName: showTimezoneString() ? 'short' : undefined,
    }).format(dateOrMomentToDate(d)), // 12/31 19:30
  // alwaysShowTimeZone forces the zone label for the rare case where an unlabeled wall clock would misread as the viewer's own.
  DATE_TIME: (
    d: DateOrMoment,
    timeZone: Timezone = undefined,
    overrideSelectedTime = false,
    alwaysShowTimeZone = false
  ) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: overrideSelectedTime ? timeZone : showNetworkTime() ? timeZone : undefined,
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            timeZoneName: alwaysShowTimeZone || showTimezoneString() ? 'short' : undefined,
          }).format(date)
        ) // 12/31/2020, 9:30 PM
      }
    } catch (e) {
      console.error('Invalid Date Time Format from DATE_TIME', { d })
    }
  },
  MONTH_DATE_TIME: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: showNetworkTime() ? timeZone : undefined,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date)
        ) // Dec 31, 9:30 PM
      }
    } catch (e) {
      console.error('Invalid Date Time Format from MONTH_DATE_TIME', {
        d,
        timeZone,
      })
    }
  },
  MONTH_DATE_YEAR_TIME: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: showNetworkTime() ? timeZone : undefined,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            year: '2-digit',
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date)
        ) // Dec 31, 24 9:30 PM
      }
    } catch (e) {
      console.error('Invalid Date Time Format from MONTH_DATE_YEAR_TIME', {
        d,
      })
    }
  },
  MONTH_DATE_YEAR: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(
      dateOrMomentToDate(d) // Dec 31, 2020
    ),
  LONG_MONTH_DATE_YEAR: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(
      dateOrMomentToDate(d) // December 31, 2020
    ),
  YEAR_MONTH_DATE: (d: DateOrMoment, timeZone: Timezone = undefined) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: showNetworkTime() ? timeZone : undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(
      dateOrMomentToDate(d) // 12/31/2020
    ),
  DAY_TIME: (d: DateOrMoment, timeZone: Timezone = undefined, overrideSelectedTime?: boolean) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: overrideSelectedTime ? timeZone : showNetworkTime() ? timeZone : undefined,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date)
        ) // Fri Dec 31 9:30
      }
    } catch (e) {
      console.error('Invalid Date Time Format from DAY_TIME', { d })
    }
  },
  WEEKDAY_TIME: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: showNetworkTime() ? timeZone : undefined,
            weekday: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: !get24HourTime(),
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date) // Fri 31 h:mm AM/PM
        )
      }
    } catch (e) {
      console.error('Invalid Date Time Format from WEEKDAY_TIME', { d })
    }
  },
  WEEKDAY_MONTH_DATE: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          weekday: 'short',
          month: 'numeric',
          day: 'numeric',
        }).format(date) // Fri 12/31
      }
    } catch (e) {
      console.error('Invalid Date Time Format from WEEKDAY_MONTH_DATE', { d })
    }
  },
  WEEKDAY_MONTH_DATE_TIME: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return amPmToUpper(
          new Intl.DateTimeFormat(undefined, {
            timeZone: showNetworkTime() ? timeZone : undefined,
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: !get24HourTime(),
            timeZoneName: showTimezoneString() ? 'short' : undefined,
          }).format(date) // Fri 12/31 h:mm AM/PM
        )
      }
    } catch (e) {
      console.error('Invalid Date Time Format from WEEKDAY_MONTH_DATE_TIME', {
        d,
      })
    }
  },
  ISO: (d: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const date = dateOrMomentToDate(d)
      if (date) {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(date)
      }
    } catch (e) {
      console.error('Invalid Date Time Format from ISO', { d })
    }
  },
  MONTH_DATE_TIME_RANGE: (start: DateOrMoment, end: DateOrMoment, timeZone: Timezone = undefined) => {
    try {
      const startDate = dateOrMomentToDate(start)
      const endDate = dateOrMomentToDate(end)

      if (startDate && endDate) {
        const formatter = new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: !get24HourTime(),
        })

        const startFormatted = amPmToUpper(formatter.format(startDate)).replace(',', '')
        const endTimeFormatter = new Intl.DateTimeFormat(undefined, {
          timeZone: showNetworkTime() ? timeZone : undefined,
          hour: 'numeric',
          minute: 'numeric',
          hour12: !get24HourTime(),
          timeZoneName: showTimezoneString() ? 'short' : undefined,
        })
        const endTimeFormatted = amPmToUpper(endTimeFormatter.format(endDate))

        return `${startFormatted} - ${endTimeFormatted}` // 9/26 10:00 AM - 10:00 PM CDT
      }
    } catch (e) {
      console.error('Invalid Date Time Format from MONTH_DATE_TIME_RANGE', {
        start,
        end,
      })
    }
  },
}

const dateOrMomentToDate = (d: DateOrMoment): Date | undefined => {
  if (!d) {
    return undefined
  }

  // Handle Moment objects
  try {
    if (moment.isMoment(d)) {
      const date = d.toDate()
      return !isNaN(date.getTime()) ? date : undefined
    }
  } catch (e) {
    console.error('Tried to convert from moment.  Found Invalid Date Format: ', { d, e })
  }

  // Handle Date objects
  if (d instanceof Date) {
    return !isNaN(d.getTime()) ? d : undefined
  }

  // Handle dayjs objects
  try {
    if (d && typeof d === 'object' && 'toDate' in d && typeof d.toDate === 'function') {
      const date = d.toDate()
      return !isNaN(date.getTime()) ? date : undefined
    }
  } catch (e) {
    console.error('Tried to convert from dayjs.  Found Invalid Date Format: ', { d, e })
  }

  // Handle strings/numbers
  try {
    const date = new Date(d)
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date
    }
  } catch (e) {
    console.error('Tried to convert with new Date.  Found Invalid Date Format: ', { d, e })
  }

  console.log(`%c Could not find a way to convert ${d} to a valid time format`, 'color: orange')
  return undefined
}

function amPmToUpper(d: string) {
  if (d?.includes(' am')) {
    d = d?.replaceAll(' am', ' AM')
  }
  if (d?.includes(' pm')) {
    d = d?.replaceAll(' pm', ' PM')
  }
  return d
}

export function getDateAtTimeString(d: DateOrMoment, timeZone: Timezone = undefined) {
  return `${formatters.MONTH_DAY(d, timeZone)} at ${formatters.TIME(d, timeZone)}`
}

export function dayjsWithTimezone(d: dayjs.ConfigType, timeZone: Timezone = undefined) {
  return dayjs(d).tz(showNetworkTime() ? timeZone : getUserTimezone())
}

export function getUserTimezone() {
  return dayjs.tz.guess()
}

// The zone times are actually being rendered in, for labelling one. Follows the same
// network-time setting the formatters do, so a header can name the clock the reader
// is looking at rather than the one the data was sent with.
export function effectiveTimezone(timeZone: Timezone = undefined): string {
  return (showNetworkTime() && timeZone) || getUserTimezone()
}

// The hour 0-23 an instant falls on, in the same zone formatters.TIME would render it
// in. Returned as a number rather than text so a column can filter and group on the
// value it displays; formatters.HOUR_OF_DAY turns it into the label.
export function hourOfDay(d: DateOrMoment, timeZone: Timezone = undefined): number {
  return Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: showNetworkTime() ? timeZone : undefined,
      hour: 'numeric',
      hourCycle: 'h23',
    }).format(dateOrMomentToDate(d))
  )
}

// Converts a date time to a specified timezone but keeps the same day/minute/hour
// i.e. 2023-01-01 12:00 in GMT will be 2023-01-01 12:00 in CST
export function getFixedZoneTime(date: dayjs.ConfigType, timeZone?: string, format = 'YYYY-MM-DD HH:mm'): Dayjs {
  const dateStr = dayjs(date).format(format)
  return dayjs.tz(dateStr, format, showNetworkTime() && timeZone ? timeZone : getUserTimezone())
}

// Serializes a picked calendar date as UTC midnight, with no timezone math, for fields the
// backend treats as a calendar date rather than an instant (e.g. supplier invoice due date).
// The Y-M-D shown in the picker is sent verbatim so it never drifts a day across timezones.
export function toCalendarDateUTC(date: dayjs.ConfigType): string | null {
  if (!date) return null
  const d = dayjs(date)
  return d.isValid() ? dayjs.utc(d.format('YYYY-MM-DD')).toISOString() : null
}

// Reads a stored calendar date into a picker value, taking the Y-M-D irrespective of any
// offset the wire value carries (naive or legacy Z-tagged UTC midnight both resolve to the
// same day). Inverse of toCalendarDateUTC.
export function parseCalendarDate(date: dayjs.ConfigType): Dayjs | undefined {
  if (!date) return undefined
  const d = dayjs.utc(date)
  return d.isValid() ? dayjs(d.format('YYYY-MM-DD')) : undefined
}

// The calendar day after today where the given zone lives, as an offset-free Dayjs so a picker
// displays and serializes that Y-M-D verbatim. Returning the zoned instant instead renders a day
// early or late for anyone whose own offset differs (see CLAUDE.md on calendar dates).
export function startOfTomorrowIn(timeZone?: string | null): Dayjs {
  return dayjs(
    dayjs()
      .tz(timeZone || getUserTimezone())
      .add(1, 'day')
      .format('YYYY-MM-DD')
  )
}

export function isBeforeTomorrowIn(day: Dayjs, timeZone?: string | null): boolean {
  return day.format('YYYY-MM-DD') < startOfTomorrowIn(timeZone).format('YYYY-MM-DD')
}

// Serializes a picked calendar date as naive midnight, for fields the backend stores
// without an offset. Unlike toCalendarDateUTC it carries no Z, so nothing downstream
// can shift it a day (see CLAUDE.md on datetime tagging).
export function toNaiveCalendarDate(date: dayjs.ConfigType): string | null {
  const d = dayjs(date)
  return d.isValid() ? `${d.format('YYYY-MM-DD')}T00:00:00` : null
}
